import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchWorkoutHistory } from "../../features/workoutHistory/workoutHistoryService";
import type { WorkoutHistoryItem } from "../../features/workoutHistory/types";
import {
  Box,
  Button,
  CircularProgress,
  Avatar,
  Tab,
  Tabs,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  CardMedia,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useGetUser, useGetCommunityActivity, useGetAchievements, useGetTransactions, useGetLoginHistory, useGetUserActivity } from "../../api/hooks/useUser";
import { useGetUserSubscription } from "../../api/hooks/useSubscription";
import { showError } from "../../helpers/toast";
import type { User, CommunityActivityItem, Achievement } from "../../api/types/user.types";
import TransactionHistory from "../../features/manageUsers/transctionHistory";
import LoginHistory from "../../features/manageUsers/loginHistory";
import SearchBar from "../../components/SearchBar";
import {
  useGetProgressPhotos,
  useGetWeightHistory,
  useGetWaterHistory,
  useGetProgressOverview
} from "../../api/hooks/useUserProgress";
import type {
  ProgressPhotoGroup,
  WeightEntry,
  WaterEntry,
} from "../../api/types/userProgress.types";


interface CustomTabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
  [key: string]: any;
}

const CustomTabPanel: React.FC<CustomTabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
};

const topTabs = ["Personal Info", "Subscription", "Workouts", "Progress", "Achievements", "Community", "Activity & Engagement", "Transaction History", "Login History"];

const COMMUNITY_ACTIVITY_TYPE = {
  COMMENT: 1,
  REPLY: 2,
  LIKE: 3,
  REPORT: 4
};

const getActivityLabel = (type: number) => {
  switch (type) {
    case COMMUNITY_ACTIVITY_TYPE.COMMENT: return "Comment";
    case COMMUNITY_ACTIVITY_TYPE.REPLY: return "Reply";
    case COMMUNITY_ACTIVITY_TYPE.LIKE: return "Like";
    case COMMUNITY_ACTIVITY_TYPE.REPORT: return "Report";
    default: return "Activity";
  }
};

const ManageUserDetail: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const userId = state?.id;
  const [value, setValue] = useState(0);
  const [workoutHistory, setWorkoutHistory] = useState([]);
  const [isLoadingWorkouts, setIsLoadingWorkouts] = useState(false);
  const [communityActivity, setCommunityActivity] = useState<CommunityActivityItem[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 500);
    return () => clearTimeout(timerId);
  }, [searchTerm]);

  // Fetch user data
  const { data: userData, isLoading: isLoadingUser, error: userError } = useGetUser(
    userId || '',
    { enabled: !!userId }
  );

  // Fetch workout history when the "Workouts" tab is active
  useEffect(() => {
    if (value === 2 && userId) {
      const getHistory = async () => {
        setIsLoadingWorkouts(true);
        try {
          const history = await fetchWorkoutHistory(userId, debouncedSearchTerm);
          setWorkoutHistory(history as any);
        } catch (error) {
          console.error("Error fetching workout history:", error);
          showError("Failed to fetch workout history");
        } finally {
          setIsLoadingWorkouts(false);
        }
      };
      getHistory();
    }
  }, [value, userId, debouncedSearchTerm]);

  // Fetch community activity when the "Community" tab is active
  const { data: communityData, isLoading: isLoadingCommunityQuery } = useGetCommunityActivity(
    userId || '',
    debouncedSearchTerm,
    { enabled: !!userId && value === 5 }
  );

  // Fetch subscription data
  const { data: subscriptionData, isLoading: isLoadingSubscription } = useGetUserSubscription(
    userId || '',
    { enabled: !!userId && value === 1 }
  );

  // Fetch achievements data
  const { data: achievementsData, isLoading: isLoadingAchievements } = useGetAchievements(
    userId || '',
    { enabled: !!userId && value === 4 }
  );

  // Fetch transaction history
  const { data: transactionsData, isLoading: isLoadingTransactions } = useGetTransactions(
    userId || '',
    { enabled: !!userId && value === 7 }
  );

  // Fetch login history
  const { data: loginHistoryData, isLoading: isLoadingLoginHistory } = useGetLoginHistory(
    userId || '',
    { enabled: !!userId && value === 8 }
  );

  // Fetch user activity
  const { data: userActivityData, isLoading: isLoadingUserActivity } = useGetUserActivity(
    userId || '',
    { enabled: !!userId && value === 6 }
  );

  useEffect(() => {
    if (communityData?.data) {
      // communityData.data is the payload [ { totalCount, list } ]
      const actualData = communityData.data as any;
      const list = (Array.isArray(actualData) ? actualData[0]?.list : actualData?.list) || [];
      setCommunityActivity(list as CommunityActivityItem[]);
    }
  }, [communityData]);

  // Fetch progress data
  const { data: weightData, isLoading: isLoadingWeight } = useGetWeightHistory(userId || '', {
    enabled: !!userId && value === 3,
  });

  const { data: photosData, isLoading: isLoadingPhotos } = useGetProgressPhotos(userId || '', {
    enabled: !!userId && value === 3
  });

  const { data: waterData, isLoading: isLoadingWater } = useGetWaterHistory(userId || '', {
    enabled: !!userId && value === 3
  });

  const { data: overviewData } = useGetProgressOverview(userId || '', {
    enabled: !!userId && value === 3
  });

  // Debug log when the tab changes to Progress
  useEffect(() => {
    if (value === 3) {
      console.log('Progress tab active, should fetch weight history');
      console.log('User ID:', userId);
      console.log('Is user ID valid:', !!userId);
    }
  }, [value, userId]);

  // Extract list from new response structure { data: { list: ... } }
  const progressPhotos = photosData?.data?.list || [];
  const weightHistory = weightData?.data?.list || [];
  const waterHistory = waterData?.data?.list || [];
  const progressOverview = (overviewData?.data as any) || {}; // Handle potential nesting variations safely

  // Derived stats for Weight
  const sortedWeight = [...weightHistory].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const latestWeight = sortedWeight.length > 0 ? sortedWeight[0].weight : 0;
  const startingWeight = sortedWeight.length > 0 ? sortedWeight[sortedWeight.length - 1].weight : 0;

  // Derived stats for Water
  // Calculate averages if needed, or use placeholders if complex logic is required
  const waterDailyGoal = 2000; // Default or from settings if available
  const sortedWater = [...waterHistory].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const lastLoggedWaterDate = sortedWater.length > 0 ? new Date(sortedWater[0].date).toLocaleDateString() : "N/A";

  // Extract subscription data
  // API response shape:
  // { data: { subscriptionDetails: { totalCount, list: [...] }, currentSubscription: {...} } }
  const subscriptionPayload = subscriptionData?.data as any;
  const subscriptionHistory = subscriptionPayload?.subscriptionDetails?.list ?? [];
  // Some responses return `currentSubscription: null` even when history has the latest plan.
  // Use first history row as fallback so details still render in the Subscription tab.
  const userSubscription = subscriptionPayload?.currentSubscription ?? subscriptionHistory?.[0] ?? null;

  const getPlanName = (plan: any) => {
    if (!plan) return "N/A";
    if (Array.isArray(plan)) return plan?.[0]?.name || "N/A";
    if (typeof plan === "object") return plan?.name || plan?._id || "N/A";
    return String(plan);
  };

  // `currentSubscription` may not include `plan` in some API responses.
  // Fallback to the first record in `subscriptionDetails.list`.
  const currentPlanName = getPlanName(userSubscription?.plan ?? subscriptionHistory?.[0]?.plan);

  // Extract achievements data
  const achievements = achievementsData?.data?.list || [];

  // Extract transaction data
  // API response shape (from your example):
  // data: [ { totalCount, list: [ { _id, totalAmount, stripeTransactionId, createdAt } ] } ]
  const transactionsPayload = transactionsData?.data as any;
  const transactionsList =
    Array.isArray(transactionsPayload)
      ? transactionsPayload?.[0]?.list ?? []
      : transactionsPayload?.list ?? transactionsPayload ?? [];

  // Extract login history data
  const loginHistoryList = (loginHistoryData?.data as any)?.list || [];

  // Extract user activity data
  const userActivity = userActivityData?.data || null;

  const user: User | null = userData?.data || null;

  // Handle error
  useEffect(() => {
    if (userError) {
      showError((userError as any)?.response?.data?.message || "Failed to load user details");
    }
  }, [userError]);

  // Redirect if no userId
  useEffect(() => {
    if (!userId && !isLoadingUser) {
      showError("No user selected. Redirecting to user list.");
      navigate("/manage-users");
    }
  }, [userId, isLoadingUser, navigate]);

  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    console.log(`Tab changed to index: ${newValue}, Tab name: ${topTabs[newValue]}`);
    setValue(newValue);
  };

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "N/A";
    }
  };

  if (isLoadingUser) {
    return (
      <>
        <div className="main_title">
          <h1>User Detail</h1>
          <p>
            <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
            <button onClick={() => navigate("/manage-users")}>Manage Users</button> - User Detail
          </p>
        </div>
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (!user) {
    return (
      <>
        <div className="main_title">
          <h1>User Detail</h1>
          <p>
            <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
            <button onClick={() => navigate("/manage-users")}>Manage Users</button> - User Detail
          </p>
        </div>
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <p>User not found.</p>
        </Box>
      </>
    );
  }

  return (
    <>
      <div className="main_title">
        <h1>User Detail</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-users")}>Manage Users</button> - User Detail
        </p>
      </div>

      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs value={value} onChange={handleChangeTabs} className="custom_tabs_links" variant="scrollable" scrollButtons="auto">
              {topTabs.map((t, i) => (
                <Tab key={i} label={t} {...a11yProps(i)} />
              ))}
            </Tabs>
          </Box>
        </div>
      </div>

      {/* Personal Info Tab */}
      <CustomTabPanel value={value} index={0}>
        <div className="cards view_page">
          <div className="table_header">
            <h2>Personal Info</h2>
            <div className="right_s">
              <Button
                className="btn btn_primary sm"
                onClick={() => navigate("/manage-users/edit", { state: { userId: user._id } })}
              >
                Edit User
              </Button>
            </div>
          </div>

          <div className="gap_p">
            <div className="w_33">
              <h6>Profile Image</h6>
              <Avatar
                src={user.image || "/static/images/user_placeholder.png"}
                alt={user.fullName}
                sx={{ width: 100, height: 100, mb: 2 }}
              />
            </div>
            <div className="w_33">
              <h6>Full Name</h6>
              <p>{user.fullName || "-"}</p>
            </div>
            <div className="w_33">
              <h6>Email</h6>
              <p>{user.email || "-"}</p>
            </div>
            <div className="w_33">
              <h6>Phone</h6>
              <p>
                {user.countryCode || ""} {user.phone || "-"}
              </p>
            </div>
            <div className="w_33">
              <h6>Gender</h6>
              <p>{user.gender === 1 ? "Male" : user.gender === 2 ? "Female" : user.gender === 3 ? "Other" : "-"}</p>
            </div>
            <div className="w_33">
              <h6>Date of Birth</h6>
              <p>
                {user.dateOfBirth
                  ? new Date(user.dateOfBirth).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })
                  : "-"}
              </p>
            </div>
            <div className="w_33">
              <h6>Status</h6>
              <p>
                <span className={user.isBlocked ? "status_blocked" : "status_active"}>
                  {user.isBlocked ? "Blocked" : "Active"}
                </span>
              </p>
            </div>
            <div className="w_33">
              <h6>Created Date</h6>
              <p>{formatDate(user.createdAt)}</p>
            </div>
            <div className="w_33">
              <h6>Last Updated</h6>
              <p>{formatDate(user.updatedAt)}</p>
            </div>
          </div>
        </div>
      </CustomTabPanel>

      {/* ---------- SUBSCRIPTION ---------- */}
      <CustomTabPanel value={value} index={1}>
        <div className="cards">
          {isLoadingSubscription ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : !userSubscription ? (
            <Box display="flex" justifyContent="center" p={4}>
              <p>No subscription found for this user.</p>
            </Box>
          ) : (
            <>
              <div className="table_header">
                <div className="left_s">
                  <div className="section_title">
                    <h2>
                      Subscription Details - <span className="c_primary">{currentPlanName}</span>
                    </h2>
                  </div>
                </div>
              </div>

              <div className="gap_p">
                <div className="w_33">
                  <h6>Current Plan</h6>
                  <p>{currentPlanName}</p>
                </div>
                <div className="w_33">
                  <h6>Start Date</h6>
                  <p>{userSubscription.startDate ? new Date(userSubscription.startDate).toLocaleDateString() : "N/A"}</p>
                </div>
                <div className="w_33">
                  <h6>End Date</h6>
                  <p>{userSubscription.endDate ? new Date(userSubscription.endDate).toLocaleDateString() : "N/A"}</p>
                </div>
                <div className="w_33">
                  <h6>Auto Renew</h6>
                  <p>
                    {typeof userSubscription.autoRenew === "boolean"
                      ? userSubscription.autoRenew
                        ? "On"
                        : "Off"
                      : userSubscription.cancelledAt
                        ? "Off"
                        : "On"}
                  </p>
                </div>
                <div className="w_33">
                  <h6>Payment Status</h6>
                  <p>{userSubscription.paymentStatus ?? (userSubscription.status != null ? String(userSubscription.status) : "N/A")}</p>
                </div>
                <div className="w_33">
                  <h6>Amount</h6>
                  <p>₹{userSubscription.amount}</p>
                </div>
              </div>

              <div className="cards mt_20">
                <h6>Subscription History</h6>
                <TableContainer className="table_container">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Plan</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Transaction ID</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {subscriptionHistory.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4}>No subscription history available</TableCell>
                        </TableRow>
                      ) : (
                        subscriptionHistory.map((h: any, i: number) => (
                          <TableRow key={i}>
                            <TableCell>
                              {h.date
                                ? new Date(h.date).toLocaleDateString()
                                : h.startDate
                                  ? new Date(h.startDate).toLocaleDateString()
                                  : h.createdAt
                                    ? new Date(h.createdAt).toLocaleDateString()
                                    : "N/A"}
                            </TableCell>
                            <TableCell>{getPlanName(h.plan)}</TableCell>
                            <TableCell>₹{h.amount ?? h.subscriptionPrice ?? "N/A"}</TableCell>
                            <TableCell>{h.stripeTransactionId ?? h.id ?? h._id ?? "N/A"}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </>
          )}
        </div>
      </CustomTabPanel>

      {/* ---------- WORKOUT HISTORY ---------- */}
      <CustomTabPanel value={value} index={2}>
        <div className="cards">
          <div className="table_header">
            <h2>Workout History</h2>
            <div className="right_s">
              <SearchBar
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                onCross={() => setSearchTerm("")}
                setDebouncedSearchTerm={setSearchTerm}
                searchTerm={searchTerm}
                placeholder="Search workouts..."
              />
            </div>
          </div>

          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Program</TableCell>
                  <TableCell>Duration (min)</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {isLoadingWorkouts ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : workoutHistory.length > 0 ? (
                  workoutHistory
                    .filter((w: WorkoutHistoryItem) =>
                      !debouncedSearchTerm ||
                      w.program?.title?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
                      w.workout?.title?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
                      w.program?.progNo?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
                    )
                    .map((w: WorkoutHistoryItem) => (
                      <TableRow hover key={w._id || Math.random()}>
                        <TableCell>{w.program?.progNo || "N/A"}</TableCell>
                        <TableCell>{formatDate(w.createdAt)}</TableCell>
                        <TableCell>
                          {w.program?.title ? `${w.program.title} (Program)` : w.workout?.title ? `${w.workout.title} (Workout)` : "N/A"}
                        </TableCell>
                        <TableCell>{w.workout?.duration ? `${w.workout.duration} min` : "N/A"}</TableCell>
                        <TableCell>
                          <span className={w.status === 1 ? "status_active" : "status_blocked"}>
                            {w.status === 1 ? "Completed" : "In Progress"}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No workout history found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </CustomTabPanel>

      {/* ---------- PROGRESS ---------- */}
      <CustomTabPanel value={value} index={3}>
        {/* Progress Overview */}
        <div className="cards">
          <div className="table_header">
            <h2>Progress Overview</h2>
          </div>
          <div className="gap_p">
            <div className="w_33">
              <h6>First Tracking Date</h6>
              <p>{progressOverview.firstTrackingDate ? new Date(progressOverview.firstTrackingDate).toLocaleDateString() : '-'}</p>
            </div>
            <div className="w_33">
              <h6>Latest Tracking Date</h6>
              <p>{progressOverview.latestTrackingDate ? new Date(progressOverview.latestTrackingDate).toLocaleDateString() : '-'}</p>
            </div>
            <div className="w_33">
              <h6>Total Progress Entries</h6>
              <p>{progressOverview.totalProgressEntries || 0}</p>
            </div>
            <div className="w_33">
              <h6>Starting Weight</h6>
              <p>{progressOverview.startingWeight} kg</p>
            </div>
            <div className="w_33">
              <h6>Latest Weight</h6>
              <p>{progressOverview.latestWeight} kg</p>
            </div>
            <div className="w_33">
              <h6>Avg Water (7 days)</h6>
              <p>{progressOverview.avgWaterLast7Days || 0} ml</p>
            </div>
          </div>
        </div>

        {/* Progress Photos */}
        <div className="cards mt_20">
          <div className="table_header">
            <h2>Progress Photos</h2>
          </div>
          {isLoadingPhotos ? (
            <CircularProgress size={24} />
          ) : progressPhotos.length === 0 ? (
            <p>No progress photos available</p>
          ) : (
            progressPhotos.map((group: ProgressPhotoGroup, index: number) => {
              const front = group.photos.find(p => p.type === 1)?.image;
              const side = group.photos.find(p => p.type === 2)?.image;
              const back = group.photos.find(p => p.type === 3)?.image;

              return (
                <Accordion key={index} defaultExpanded={index === 0}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <strong>{new Date(group.date).toLocaleDateString()}</strong>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <div style={{ position: "relative" }}>
                          <CardMedia
                            component="img"
                            image={front || "/static/images/placeholder.jpg"}
                            alt="Front"
                            sx={{ width: "100%", height: 300, objectFit: "cover", borderRadius: 2 }}
                          />
                          <div style={{ position: "absolute", top: 8, left: 8, background: "rgba(0,0,0,0.7)", color: "white", padding: "4px 8px", borderRadius: 4 }}>
                            Front
                          </div>
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <div style={{ position: "relative" }}>
                          <CardMedia
                            component="img"
                            image={side || "/static/images/placeholder.jpg"}
                            alt="Side"
                            sx={{ width: "100%", height: 300, objectFit: "cover", borderRadius: 2 }}
                          />
                          <div style={{ position: "absolute", top: 8, left: 8, background: "rgba(0,0,0,0.7)", color: "white", padding: "4px 8px", borderRadius: 4 }}>
                            Side
                          </div>
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <div style={{ position: "relative" }}>
                          <CardMedia
                            component="img"
                            image={back || "/static/images/placeholder.jpg"}
                            alt="Back"
                            sx={{ width: "100%", height: 300, objectFit: "cover", borderRadius: 2 }}
                          />
                          <div style={{ position: "absolute", top: 8, left: 8, background: "rgba(0,0,0,0.7)", color: "white", padding: "4px 8px", borderRadius: 4 }}>
                            Back
                          </div>
                        </div>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );
            })
          )}
        </div>

        {/* Weight Tracking */}
        <div className="cards mt_20">
          <div className="table_header">
            <h2>Weight Tracking</h2>
          </div>

          {/* Weight Summary */}
          <div className="gap_p mb_20">
            <div className="w_33">
              <h6>Starting Weight</h6>
              <p>{progressOverview.startingWeight || startingWeight} kg</p>
            </div>
            <div className="w_33">
              <h6>Latest Weight</h6>
              <p>{progressOverview.latestWeight || latestWeight} kg</p>
            </div>
            <div className="w_33">
              <h6>Net Change</h6>
              <p style={{ color: ((progressOverview.latestWeight || 0) - (progressOverview.startingWeight || 0)) < 0 ? "green" : "red" }}>
                {progressOverview.latestWeight && progressOverview.startingWeight
                  ? `${(progressOverview.latestWeight - progressOverview.startingWeight) > 0 ? "+" : ""}${(progressOverview.latestWeight - progressOverview.startingWeight).toFixed(1)}`
                  : '-'} kg
              </p>
            </div>
          </div>

          {/* Weight History */}
          <div>
            <h6 style={{ marginBottom: 16 }}>Weight History</h6>
            <TableContainer className="table_container">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Weight (kg)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoadingWeight ? (
                    <TableRow>
                      <TableCell colSpan={2} align="center"><CircularProgress size={20} /></TableCell>
                    </TableRow>
                  ) : sortedWeight.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2}>No weight tracking data available</TableCell>
                    </TableRow>
                  ) : (
                    sortedWeight.map((entry: WeightEntry) => (
                      <TableRow key={entry._id} hover>
                        <TableCell>{new Date(entry.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell>{entry.weight} kg</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>

        {/* Water Tracking */}
        <div className="cards mt_20">
          <div className="table_header">
            <h2>Water Tracking</h2>
          </div>

          {/* Water Summary */}
          <div className="gap_p mb_20">
            <div className="w_33">
              <h6>Daily Goal</h6>
              <p>{progressOverview.dailyWaterGoal || waterDailyGoal} ml</p>
            </div>
            <div className="w_33">
              <h6>Average Intake (7 days)</h6>
              <p>{progressOverview.avgWaterLast7Days || 0} ml</p>
            </div>
            <div className="w_33">
              <h6>Average Intake (30 days)</h6>
              <p>{progressOverview.avgWaterLast30Days || 0} ml</p>
            </div>
            <div className="w_33">
              <h6>Last Logged Date</h6>
              <p>{progressOverview.waterLastLoggedDate ? new Date(progressOverview.waterLastLoggedDate).toLocaleDateString() : lastLoggedWaterDate}</p>
            </div>
          </div>

          {/* Water History */}
          <div>
            <h6 style={{ marginBottom: 16 }}>Water Intake History</h6>
            <TableContainer className="table_container">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Amount Logged (ml)</TableCell>
                    <TableCell>Goal Met?</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoadingWater ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center"><CircularProgress size={20} /></TableCell>
                    </TableRow>
                  ) : sortedWater.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3}>No water tracking data available</TableCell>
                    </TableRow>
                  ) : (
                    sortedWater.map((entry: WaterEntry) => {
                      const goalMet = entry.waterIntake >= waterDailyGoal;
                      return (
                        <TableRow key={entry._id} hover>
                          <TableCell>{new Date(entry.date).toLocaleDateString()}</TableCell>
                          <TableCell>{entry.waterIntake} ml</TableCell>
                          <TableCell>
                            <span style={{
                              color: goalMet ? "green" : "red",
                              fontWeight: "bold"
                            }}>
                              {goalMet ? "Yes" : "No"}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>
      </CustomTabPanel>

      {/* ---------- ACHIEVEMENTS ---------- */}
      <CustomTabPanel value={value} index={4}>
        <div className="cards">
          <div className="table_header">
            <h2>Achievements</h2>
          </div>

          <div className="gap_p">
            {isLoadingAchievements ? (
              <Box display="flex" justifyContent="center" sx={{ width: "100%" }} p={4}>
                <CircularProgress size={24} />
              </Box>
            ) : achievements.length === 0 ? (
              <p>No achievements yet</p>
            ) : (
              achievements.map((a: Achievement) => (
                <div className="w_33" key={a._id}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', border: '1px solid #efefef', borderRadius: '8px', marginBottom: '12px' }}>
                    {a.badges.image && (
                      <img src={a.badges.image} alt={a.badges.title} style={{ width: '40px', height: '40px', borderRadius: '50%' }} />
                    )}
                    <div>
                      <h6 style={{ margin: 0 }}>{a.badges.title}</h6>
                      <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                        {a.createdAt ? new Date(a.createdAt).toLocaleDateString() : "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </CustomTabPanel>

      {/* ---------- COMMUNITY ---------- */}
      <CustomTabPanel value={value} index={5}>
        <div className="cards">
          <div className="table_header">
            <h2>Community Activity</h2>
            <div className="right_s">
              <SearchBar
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                onCross={() => setSearchTerm("")}
                setDebouncedSearchTerm={setSearchTerm}
                searchTerm={searchTerm}
                placeholder="Search community..."
              />
            </div>
          </div>

          <div className="gap_p">
            {isLoadingCommunityQuery ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : communityActivity.length === 0 ? (
              <p>No community posts/comments</p>
            ) : (
              communityActivity
                .map((c: CommunityActivityItem) => (
                  <div key={c._id} className="w_100" style={{ marginBottom: 16, padding: '12px', border: '1px solid #efefef', borderRadius: '8px' }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                      <Box>
                        <strong style={{ marginRight: 8 }}>{getActivityLabel(c.activityType)}</strong>
                        {c.community && (
                          <span style={{ fontSize: '13px', color: '#666' }}>
                            in <b>{c.community.name}</b>
                          </span>
                        )}
                      </Box>
                      <span style={{ fontSize: '12px', color: '#999' }}>
                        {c.createdAt ? new Date(c.createdAt).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric"
                        }) : "N/A"}
                      </span>
                    </div>
                    <div style={{ margin: 0 }}>
                      {c.comment?.text && (c.comment.text.startsWith("http://") || c.comment.text.startsWith("https://")) ? (
                        <img
                          src={c.comment.text}
                          alt="Comment attachment"
                          style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px', marginTop: '4px', objectFit: 'contain', display: 'block' }}
                        />
                      ) : (
                        <p style={{ margin: 0 }}>{c.comment?.text || "N/A"}</p>
                      )}
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>
      </CustomTabPanel>


      {/* ---------- ACTIVITY & ENGAGEMENT ---------- */}
      <CustomTabPanel value={value} index={6}>
        {/* Activity Section */}
        <div className="cards">
          <div className="table_header">
            <h2>Activity</h2>
          </div>
          {isLoadingUserActivity ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : !userActivity ? (
            <Box display="flex" justifyContent="center" p={4}>
              <p>No activity data found.</p>
            </Box>
          ) : (
            <>
              <div className="gap_p">
                <div className="w_33">
                  <h6>Total Workouts Completed</h6>
                  <p>{userActivity.totalWorkoutCompleted || 0}</p>
                </div>
                <div className="w_33">
                  <h6>Total Programs Completed</h6>
                  <p>{userActivity.totalProgramCompleted || 0}</p>
                </div>
                <div className="w_33">
                  <h6>Last Completed Workout</h6>
                  <p>
                    <strong>{userActivity.latestCompletedWorkout?.workoutName || "N/A"}</strong>
                    <br />
                    <span style={{ color: "#666" }}>{formatDate(userActivity.latestCompletedWorkout?.completedAt)}</span>
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Program Enrollment Section */}
        {userActivity && (
          <div className="cards mt_20">
            <div className="table_header">
              <h2>Program Enrollment</h2>
            </div>

            {/* Current Program */}
            <div className="mb_20">
              <h6 style={{ marginBottom: 16, fontSize: "18px", fontWeight: "bold" }}>Current Program</h6>
              {!userActivity.currentProgram ? (
                <p>No active program</p>
              ) : (
                <div className="cards" style={{ marginBottom: 16, padding: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <h6 style={{ fontSize: "16px", margin: 0 }}>{userActivity.currentProgram.programName}</h6>
                    <span style={{ color: "#1976d2", fontWeight: "bold" }}>{userActivity.currentProgram.completionPercentage}%</span>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span>Progress: {userActivity.currentProgram.completedWorkoutCount} / {userActivity.currentProgram.totalWorkoutCount} workouts</span>
                    </div>
                    <div style={{ height: 8, background: "#f0f0f0", borderRadius: 6, marginTop: 6 }}>
                      <div
                        style={{
                          width: `${userActivity.currentProgram.completionPercentage}%`,
                          height: "100%",
                          background: "#1976d2",
                          borderRadius: 6
                        }}
                      />
                    </div>
                  </div>
                  <div className="gap_p" style={{ marginTop: 12 }}>
                    <div className="w_50">
                      <h6 style={{ fontSize: "14px", marginBottom: 4 }}>Start Date</h6>
                      <p style={{ margin: 0 }}>{formatDate(userActivity.currentProgram.startDate)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </CustomTabPanel>
      -
      {/* Transaction History Tab */}
      <CustomTabPanel value={value} index={7}>
        <div className="cards">
          <div className="table_header">
            <h2>Transaction History</h2>
          </div>
          {isLoadingTransactions ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TransactionHistory
              hideUserColumn={true}
              transactions={transactionsList.map((tx: any) => {
                let formattedDate = "N/A";
                const dateToFormat = tx.createdAt || tx.date;
                if (dateToFormat) {
                  try {
                    formattedDate = new Date(dateToFormat).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric"
                    });
                    if (formattedDate === "Invalid Date") {
                      formattedDate = dateToFormat;
                    }
                  } catch (e) {
                    formattedDate = dateToFormat;
                  }
                }

                return {
                  id: tx._id || tx.id,
                  date: formattedDate,
                  // API provides `stripeTransactionId`, TransactionHistory expects `receiptId`
                  receiptId: tx.stripeTransactionId || tx.receiptId || "N/A",
                  // Plan is not present in the sample payload; keep placeholder
                  plan: tx.plan || tx.subscriptionPlan || "N/A",
                };
              })}
            />
          )}
        </div>
      </CustomTabPanel>


      {/* Login History Tab */}
      <CustomTabPanel value={value} index={8}>
        <div className="cards">
          <div className="table_header">
            <h2>Login History</h2>
          </div>
          {isLoadingLoginHistory ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <LoginHistory
              loginHistory={(loginHistoryList as any[]).map((item: any) => ({
                id: item._id || item.id,
                date: item.createdAt ? new Date(item.createdAt).toLocaleString() : (item.date || item.loginTime),
                device: `${item.deviceType || 'Unknown'}`,
                ip: `${item.ip || '--'}`,
              }))}
            />
          )}
        </div>
      </CustomTabPanel>
    </>
  );
};

export default ManageUserDetail;
